import { useCallback, useEffect, useState } from 'react'
import { experienciasApi } from '../api/experiencias'
import Boton from './Boton'
import VistaPreviaInvitacion from './VistaPreviaInvitacion'
import { colors, radius, space, sx, text } from '../design/tokens'

/**
 * Invitaciones de una experiencia (Martin, 24 sep 2026), en la ficha de
 * escritorio: la TARJETA (nace con el alta; acá se retoca) y los INVITADOS
 * (una fila por nota de venta viva, con su link personal y su estado).
 *
 * Todo lo que es dato de la experiencia (título, fecha, turno, menú, foto) no
 * se edita acá: la tarjeta lo lee de la experiencia. Acá solo va lo propio de
 * la invitación.
 */

export const ESTADOS_INVITACION = {
  // Solo lo que LGR sabe: si el cliente abrió el link y si confirmó. Si se lo
  // mandaron o no, no se deduce (copiar o abrir el chat no es enviar).
  sin_abrir: { label: 'Sin abrir', bg: colors.borderSoft, color: colors.textSec },
  abierta: { label: 'Abrió', bg: colors.infoBg, color: colors.info },
  confirmada: { label: 'Confirmó', bg: colors.okBg, color: colors.ok },
}

export const fechaHora = (iso) => {
  if (!iso) return ''
  const d = new Date(iso)
  const p = (n) => String(n).padStart(2, '0')
  return `${p(d.getDate())}/${p(d.getMonth() + 1)} ${p(d.getHours())}:${p(d.getMinutes())}`
}

/** El momento que se muestra debajo del estado: el del estado actual. */
export const momentoDe = (inv) =>
  inv.confirmada_at || inv.abierta_at || null

export function ChipEstado({ estado }) {
  const c = ESTADOS_INVITACION[estado] || ESTADOS_INVITACION.sin_abrir
  return (
    <span style={{
      display: 'inline-block', padding: '1px 8px', borderRadius: radius.pill,
      fontSize: text.meta, fontWeight: 600, background: c.bg, color: c.color, whiteSpace: 'nowrap',
    }}>{c.label}</span>
  )
}

export default function InvitacionExperiencia({ experienciaId, urlPrimerLink, urlPublica, titulo }) {
  const [datos, setDatos] = useState(null)
  const [error, setError] = useState('')

  const cargar = useCallback(async () => {
    try {
      const { data } = await experienciasApi.invitados(experienciaId)
      setDatos(data)
      setError('')
    } catch {
      setError('No se pudieron cargar las invitaciones.')
    }
  }, [experienciaId])

  useEffect(() => { cargar() }, [cargar])

  if (error) return <Card titulo="La invitación"><p style={estilos.error}>{error}</p></Card>
  if (!datos) return <Card titulo="La invitación"><p style={estilos.ayuda}>Cargando…</p></Card>

  return (
    <>
      <Tarjeta
        experienciaId={experienciaId}
        inicial={datos.tarjeta}
        previaInicial={datos.vista_previa}
        casaVg={datos.casa_vg}
        linkPrueba={datos.invitados[0]?.link || urlPrimerLink}
        onGuardada={(tarjeta) => setDatos((d) => ({ ...d, tarjeta }))}
      />
      <Invitados experienciaId={experienciaId} datos={datos} onCambio={cargar} />
      {urlPublica && <TarjetaCompra url={`${urlPublica}/tarjeta`} titulo={titulo} />}
    </>
  )
}

function Tarjeta({ experienciaId, inicial, previaInicial, casaVg, linkPrueba, onGuardada }) {
  const [t, setT] = useState(inicial)
  const [guardando, setGuardando] = useState(false)
  const [msg, setMsg] = useState(null)
  const [previa, setPrevia] = useState(previaInicial)
  const [foco, setFoco] = useState(null)
  const set = (k, v) => setT((x) => ({ ...x, [k]: v }))
  const cambio = JSON.stringify(t) !== JSON.stringify(inicial)
  // Cada campo avisa qué parte de la vista previa está tocando.
  const enfoque = (k) => ({ onFocus: () => setFoco(k), onBlur: () => setFoco(null) })

  // La vista previa la arma LGR (misma regla que la tarjeta real) con lo que
  // se está escribiendo, sin guardar.
  useEffect(() => {
    if (!cambio) { setPrevia(previaInicial); return }
    const id = setTimeout(async () => {
      try {
        const { data } = await experienciasApi.vistaPreviaTarjeta(experienciaId, t)
        setPrevia(data.vista_previa)
      } catch { /* un valor inválido a medio escribir: queda la última vista */ }
    }, 350)
    return () => clearTimeout(id)
  }, [t, cambio, experienciaId, previaInicial])

  const guardar = async () => {
    setGuardando(true); setMsg(null)
    try {
      const { data } = await experienciasApi.guardarTarjeta(experienciaId, t)
      setT(data.tarjeta)
      if (data.vista_previa) setPrevia(data.vista_previa)
      onGuardada(data.tarjeta)
      setMsg({ ok: true, texto: 'Tarjeta guardada.' })
    } catch (e) {
      const errores = e?.response?.data?.errors
      setMsg({ ok: false, texto: errores ? Object.values(errores).flat().join(' · ') : 'No se pudo guardar la tarjeta.' })
    } finally { setGuardando(false) }
  }

  return (
    <Card
      titulo="La invitación"
      accion={linkPrueba && (
        <a href={linkPrueba} target="_blank" rel="noreferrer" style={estilos.linkBoton}>Ver la tarjeta ↗</a>
      )}
    >
      <p style={{ ...estilos.ayuda, marginTop: 0, marginBottom: space.lg }}>
        Nace con el alta de la experiencia. Título, fecha, turno, menú y foto salen de la experiencia:
        si cambian arriba, cambian en la tarjeta.
      </p>
      <div style={estilos.conPrevia}>
      <div>
      <div style={estilos.grillaCampos}>
        <Campo label="Encabezado" id="inv-encabezado" ayuda="Renglón verde arriba del título.">
          <input data-nocaps id="inv-encabezado" {...enfoque('encabezado')} style={sx.input} value={t.encabezado || ''} onChange={(e) => set('encabezado', e.target.value)} />
        </Campo>
        <Campo label="Texto de la cuenta regresiva" id="inv-cuenta" ayuda="Arriba de los números (días, horas, minutos).">
          <input data-nocaps id="inv-cuenta" {...enfoque('cuenta_etiqueta')} style={sx.input} value={t.cuenta_etiqueta || ''} onChange={(e) => set('cuenta_etiqueta', e.target.value)} />
        </Campo>
        <Campo label="Frase" id="inv-frase" ancho ayuda="Debajo del título, en itálica. Nace con el resumen de la experiencia.">
          <textarea data-nocaps id="inv-frase" {...enfoque('frase')} rows={2} style={estilos.area} value={t.frase || ''} onChange={(e) => set('frase', e.target.value)} />
        </Campo>
        <Campo label="Dirección" id="inv-dir" ayuda={casaVg ? 'Casa VG: siempre esta dirección.' : null}>
          <input data-nocaps id="inv-dir" {...enfoque('direccion')} style={sx.input} value={t.direccion || ''} disabled={casaVg}
            onChange={(e) => set('direccion', e.target.value)} placeholder="Calle y número, localidad" />
        </Campo>
        <Campo label="Parking" id="inv-park" ayuda={casaVg ? 'Casa VG: siempre este parking.' : null}>
          <input data-nocaps id="inv-park" {...enfoque('parking')} style={sx.input} value={t.parking || ''} disabled={casaVg}
            onChange={(e) => set('parking', e.target.value)} placeholder="Opcional" />
        </Campo>
        <Campo label="Frase del final" id="inv-nota" ancho ayuda="Debajo del botón, en itálica.">
          <input data-nocaps id="inv-nota" {...enfoque('nota')} style={sx.input} value={t.nota || ''} onChange={(e) => set('nota', e.target.value)} />
        </Campo>
        <Campo label="WhatsApp que recibe las confirmaciones" id="inv-wa" ancho
          ayuda="No se ve en la tarjeta: a este número llega el aviso de cada confirmación. Vacío = no se avisa. Con código de país, por ejemplo 54 9 11 4024-3373.">
          <input data-nocaps id="inv-wa" {...enfoque('whatsapp_aviso')} style={{ ...sx.input, maxWidth: 260 }} inputMode="tel" value={t.whatsapp_aviso || ''}
            onChange={(e) => set('whatsapp_aviso', e.target.value)} placeholder="Sin aviso" />
        </Campo>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: space.lg, marginTop: space.lg }}>
        <Boton variante="secondary" onClick={guardar} disabled={guardando || !cambio}>{guardando ? 'Guardando…' : 'Guardar la tarjeta'}</Boton>
        {msg && <span style={{ fontSize: text.sec, color: msg.ok ? colors.ok : colors.err }}>{msg.texto}</span>}
      </div>
      </div>
      <div style={{ position: 'sticky', top: space.lg }}>
        <p style={{ ...estilos.ayuda, margin: `0 0 ${space.sm}px` }}>
          Vista previa{cambio ? ' · sin guardar' : ''}. En verde, lo que cambia el campo que estás editando.
        </p>
        <VistaPreviaInvitacion tarjeta={previa} foco={foco} />
      </div>
      </div>
    </Card>
  )
}

function Invitados({ experienciaId, datos, onCambio }) {
  const [copiado, setCopiado] = useState(null)
  const { resumen, invitados } = datos

  const copiar = async (clave, texto) => {
    try {
      await navigator.clipboard.writeText(texto)
      setCopiado(clave)
      setTimeout(() => setCopiado(null), 1800)
    } catch { window.prompt('Copialo a mano:', texto) }
  }

  const copiarTodos = async () => {
    const { data } = await experienciasApi.linksInvitados(experienciaId)
    copiar('todos', data.texto)
  }

  return (
    <Card
      titulo="Invitados"
      accion={invitados.length > 0 && (
        <Boton variante="ghost" onClick={copiarTodos}>{copiado === 'todos' ? '✓ Copiados' : 'Copiar link de todos'}</Boton>
      )}
    >
      <div style={estilos.resumen}>
        <Numero valor={resumen.lugares} label="lugares vendidos" />
        <Numero valor={resumen.compras} label="compras" />
        <Numero valor={resumen.confirmaron} label="confirmaron" />
        <Numero valor={resumen.falta_confirmar} label="falta confirmar" />
      </div>

      {invitados.length === 0 ? (
        <p style={estilos.ayuda}>Todavía no hay compras de esta experiencia. Cada nota de venta que entre suma un invitado.</p>
      ) : (
        <div style={{ overflowX: 'auto', marginTop: space.lg }}>
          <table style={estilos.tabla}>
            <thead>
              <tr>
                <th style={estilos.th}>NV</th>
                <th style={estilos.th}>Cliente</th>
                <th style={{ ...estilos.th, textAlign: 'right' }}>Lug.</th>
                <th style={estilos.th}>Turno</th>
                <th style={estilos.th}>Estado</th>
                <th style={estilos.th}>Enviar</th>
              </tr>
            </thead>
            <tbody>
              {invitados.map((inv) => (
                <tr key={inv.id}>
                  <td style={{ ...estilos.td, ...sx.mono, fontSize: text.sec }}>{inv.nota_venta.numero}</td>
                  <td style={estilos.td}>
                    {inv.nombre || '—'}
                    <span style={estilos.sub}>{inv.telefono || 'sin teléfono'}</span>
                  </td>
                  <td style={{ ...estilos.td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{inv.cantidad}</td>
                  <td style={{ ...estilos.td, ...sx.mono, fontSize: text.sec }}>{inv.turno || '—'}</td>
                  <td style={estilos.td}>
                    <ChipEstado estado={inv.estado} />
                    {momentoDe(inv) && <span style={estilos.sub}>{fechaHora(momentoDe(inv))}</span>}
                  </td>
                  <td style={{ ...estilos.td, whiteSpace: 'nowrap' }}>
                    {inv.whatsapp_url && (
                      <a href={inv.whatsapp_url} target="_blank" rel="noreferrer" style={estilos.accion}>WhatsApp</a>
                    )}{' '}
                    <button type="button" onClick={() => copiar(inv.id, inv.link)} style={estilos.accion}>
                      {copiado === inv.id ? '✓ Copiado' : 'Copiar'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  )
}

/**
 * La tarjeta de COMPRA (Martin, 24 sep 2026): un link por experiencia para
 * mandar por WhatsApp a quien todavía no compró. Elige turno y lugares ahí
 * mismo y va directo al checkout. Mismo diseño que la invitación.
 */
export function mensajeTarjetaCompra(titulo, url) {
  return `Te comparto ${titulo ? `«${titulo}»` : 'esta experiencia'}. Podés reservar tu lugar acá: ${url}`
}

function TarjetaCompra({ url, titulo }) {
  const [copiado, setCopiado] = useState(false)
  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(url)
      setCopiado(true)
      setTimeout(() => setCopiado(false), 1800)
    } catch { window.prompt('Copialo a mano:', url) }
  }
  return (
    <Card
      titulo="Tarjeta de compra"
      accion={<a href={url} target="_blank" rel="noreferrer" style={estilos.linkBoton}>Ver la tarjeta ↗</a>}
    >
      <p style={{ ...estilos.ayuda, marginTop: 0, marginBottom: space.lg }}>
        Para mandar a quien todavía no compró: elige horario y lugares en la tarjeta y va directo a pagar.
        Un solo link para todos.
      </p>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: space.md }}>
        <span style={{ ...sx.mono, fontSize: text.sec, color: colors.textSub, wordBreak: 'break-all' }}>{url}</span>
        <button type="button" onClick={copiar} style={estilos.accion}>{copiado ? '✓ Copiado' : 'Copiar'}</button>
        <a href={`https://wa.me/?text=${encodeURIComponent(mensajeTarjetaCompra(titulo, url))}`} target="_blank" rel="noreferrer" style={estilos.accion}>
          WhatsApp
        </a>
      </div>
    </Card>
  )
}

function Numero({ valor, label }) {
  return (
    <div style={{ display: 'grid' }}>
      <b style={{ fontSize: text.title, fontVariantNumeric: 'tabular-nums', color: colors.text }}>{valor}</b>
      <span style={{ fontSize: text.meta, color: colors.textSec, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</span>
    </div>
  )
}

function Card({ titulo, accion, children }) {
  return (
    <section style={{ ...sx.card, marginBottom: space.xl }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: space.lg }}>
        <h3 style={sx.cardTitle}>{titulo}</h3>
        {accion && <div style={{ marginLeft: 'auto' }}>{accion}</div>}
      </div>
      {children}
    </section>
  )
}

function Campo({ label, id, ancho, ayuda, children }) {
  return (
    <div style={{ display: 'grid', gap: space.xs, alignContent: 'start', gridColumn: ancho ? '1 / -1' : undefined }}>
      <label htmlFor={id} style={{ fontSize: text.meta, fontWeight: 600, color: colors.textSec, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {label}
      </label>
      {children}
      {ayuda && <span style={estilos.ayuda}>{ayuda}</span>}
    </div>
  )
}

const estilos = {
  grilla: { display: 'grid', gap: space.lg, gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))' },
  grillaCampos: { display: 'grid', gap: space.lg, gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' },
  conPrevia: { display: 'grid', gap: space['2xl'], gridTemplateColumns: 'minmax(0, 1fr) 300px', alignItems: 'start' },
  area: { ...sx.input, width: '100%', fontFamily: 'inherit', resize: 'vertical', lineHeight: 1.5 },
  ayuda: { fontSize: text.meta, color: colors.textMeta, margin: 0 },
  error: { fontSize: text.sec, color: colors.err, margin: 0 },
  linkBoton: {
    display: 'inline-flex', alignItems: 'center', minHeight: 28, padding: '0 10px', borderRadius: radius.sm,
    fontSize: text.sec, fontWeight: 600, textDecoration: 'none',
    color: colors.link, background: colors.linkBg, border: `1px solid ${colors.linkBorder}`,
  },
  resumen: {
    display: 'flex', flexWrap: 'wrap', gap: space['2xl'], padding: `${space.md}px ${space.lg}px`,
    background: colors.bgSoft, border: `1px solid ${colors.borderSoft}`, borderRadius: radius.md,
  },
  tabla: { width: '100%', borderCollapse: 'collapse', fontSize: text.body },
  th: {
    textAlign: 'left', fontSize: text.meta, fontWeight: 600, color: colors.textSec, textTransform: 'uppercase',
    letterSpacing: '0.05em', padding: '6px 8px', borderBottom: `1px solid ${colors.border}`, whiteSpace: 'nowrap',
  },
  td: { padding: '7px 8px', borderBottom: `1px solid ${colors.borderSoft}`, verticalAlign: 'middle' },
  sub: { display: 'block', fontSize: text.meta, color: colors.textMeta },
  accion: {
    display: 'inline-flex', alignItems: 'center', minHeight: 28, padding: '0 10px', borderRadius: radius.sm,
    fontSize: text.sec, fontWeight: 600, color: colors.textSub, background: colors.bg,
    border: `1px solid ${colors.border}`, cursor: 'pointer', textDecoration: 'none', fontFamily: 'inherit',
  },
}
